/**
 * BottomNav — Alouvya parent / staff mobile bottom navigation.
 *
 * Parent (per upgrade spec): 4 tabs — Home · Messages · Calendar · More.
 * Active tab: primary purple icon + label with soft purple pill background.
 *
 * Staff: 5 tabs — Live · Home · Today · Letters · Share/Trust.
 * Admin: hidden (admin uses sidebar).
 */
import { NavLink, useLocation } from "react-router-dom";
import {
  Home,
  Calendar,
  Mail,
  Send,
  Sparkles,
  LayoutGrid,
  MoreHorizontal,
  Shield,
} from "lucide-react";
import { useAuth } from "../lib/auth";

const item = ({ isActive }) =>
  `flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-2xl transition-colors duration-200 ${
    isActive
      ? "text-[#1F3A2A] bg-[#E6EFE8]"
      : "text-[#6B7280] hover:text-[#1A1B2E]"
  }`;

export default function BottomNav() {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return null;
  // Hide bottom nav inside the admin panel (uses sidebar).
  if (location.pathname.startsWith("/admin")) return null;
  const isCaregiver = user.role === "caregiver";
  const isAdmin = user.role === "admin";
  const isParent = user.role === "parent";

  return (
    <nav
      data-testid="bottom-nav"
      className="fixed left-0 right-0 bottom-3 sm:bottom-6 z-40 pointer-events-none"
    >
      <div className="mx-auto max-w-md sm:max-w-lg px-4">
        <div className="pointer-events-auto bg-white/95 backdrop-blur-xl rounded-full border border-[#EEF0F4] shadow-[0_8px_30px_rgba(16,24,40,0.08)] flex items-center justify-around p-1.5">
          {isAdmin ? (
            <>
              <NavLink to="/staff" data-testid="bottom-nav-staff" className={item}>
                <LayoutGrid size={20} strokeWidth={1.5} />
                <span className="text-[10px] tracking-wide font-medium">Live</span>
              </NavLink>
              <NavLink to="/workspace" data-testid="bottom-nav-workspace" className={item}>
                <Home size={20} strokeWidth={1.5} />
                <span className="text-[10px] tracking-wide font-medium">Center</span>
              </NavLink>
              <NavLink to="/calendar" data-testid="bottom-nav-calendar" className={item}>
                <Calendar size={20} strokeWidth={1.5} />
                <span className="text-[10px] tracking-wide font-medium">Calendar</span>
              </NavLink>
              <NavLink to="/more" data-testid="bottom-nav-more" className={item}>
                <MoreHorizontal size={20} strokeWidth={1.5} />
                <span className="text-[10px] tracking-wide font-medium">More</span>
              </NavLink>
            </>
          ) : isParent ? (
            <>
              <NavLink to="/home" end data-testid="bottom-nav-home" className={item}>
                <Home size={20} strokeWidth={1.5} />
                <span className="text-[10px] tracking-wide font-medium">Home</span>
              </NavLink>
              <NavLink to="/messages" data-testid="bottom-nav-messages" className={item}>
                <Mail size={20} strokeWidth={1.5} />
                <span className="text-[10px] tracking-wide font-medium">Messages</span>
              </NavLink>
              <NavLink to="/calendar" data-testid="bottom-nav-calendar" className={item}>
                <Calendar size={20} strokeWidth={1.5} />
                <span className="text-[10px] tracking-wide font-medium">Calendar</span>
              </NavLink>
              <NavLink to="/more" data-testid="bottom-nav-more" className={item}>
                <MoreHorizontal size={20} strokeWidth={1.5} />
                <span className="text-[10px] tracking-wide font-medium">More</span>
              </NavLink>
            </>
          ) : (
            <>
              <NavLink to="/staff" data-testid="bottom-nav-staff" className={item}>
                <LayoutGrid size={20} strokeWidth={1.5} />
                <span className="text-[10px] tracking-wide font-medium">Live</span>
              </NavLink>
              <NavLink to="/home" end data-testid="bottom-nav-home" className={item}>
                <Home size={20} strokeWidth={1.5} />
                <span className="text-[10px] tracking-wide font-medium">Home</span>
              </NavLink>
              <NavLink to="/moments" data-testid="bottom-nav-moments" className={item}>
                <Sparkles size={20} strokeWidth={1.5} />
                <span className="text-[10px] tracking-wide font-medium">Today</span>
              </NavLink>
              <NavLink to="/messages" data-testid="bottom-nav-messages" className={item}>
                <Mail size={20} strokeWidth={1.5} />
                <span className="text-[10px] tracking-wide font-medium">Letters</span>
              </NavLink>
              {isCaregiver && (
                <NavLink to="/caregiver" data-testid="bottom-nav-share" className={item}>
                  <Send size={20} strokeWidth={1.5} />
                  <span className="text-[10px] tracking-wide font-medium">Share</span>
                </NavLink>
              )}
              <NavLink to="/trust" data-testid="bottom-nav-trust" className={item}>
                <Shield size={20} strokeWidth={1.5} />
                <span className="text-[10px] tracking-wide font-medium">Trust</span>
              </NavLink>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
